import { Feature } from '@tencent/workbench';
import type { IChartAxisSettingsShower } from '@tencent/chart-drawer-components/lib/interface';
import { IChartAxisSettingsShower as ChartAxisSettingsShower } from '@tencent/chart-drawer-components/lib/interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import type { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import ChartBaseDrawerFeature from '@src/framework/workbench/drawer/ui/chart/chart-base-drawer-feature';
import { IAxisProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis.interface';
import { INPUT_PROPS_CALLBACK_KEY } from '../../../constants';
import { SLIDE_ADAPTER } from '@src/framework/workbench/editor-adpater/slide-adapter';

@Feature({
    name: 'ChartAxisShower',
    adapters: [SLIDE_ADAPTER],
    provide: ChartAxisSettingsShower,
})
export class ValueAxisShower extends ChartBaseDrawerFeature implements IChartAxisSettingsShower {
    private logBaseValue?: number;

    public constructor() {
        super();
        this.addInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.CHART_VALUE_AXIS);
    }

    public initPropsCallback = (): void => {
        this.logBaseValue = this.getLogBaseInfo();
    };
    /**
     * 获取最小值
     */
    public getMin = (): number | undefined => {
        const model = this.getSelectModel();
        if (model?.type !== UIModelType.CHART) return;
        const axis = model.getSelectedElementProperty() as IAxisProperty;
        if (typeof axis?.scaling?.min === 'number') {
            const minObj = { min: axis.scaling.min };
            return this.getShownValue(INPUT_PROPS_CALLBACK_KEY.CHART_VALUE_AXIS, minObj) as number;
        }
        return;
    };

    /**
     * 获取最大值
     */
    public getMax = (): number | undefined => {
        const model = this.getSelectModel() as IChartModel;
        if (model?.type !== UIModelType.CHART) return;
        const axis = model.getSelectedElementProperty() as IAxisProperty;
        if (typeof axis?.scaling?.max !== 'number') return;
        const maxObj = { max: axis.scaling.max };
        return this.getShownValue(INPUT_PROPS_CALLBACK_KEY.CHART_VALUE_AXIS, maxObj) as number;
    };

    /**
     * 获取格式
     */
    public getFormat = (): string | undefined => {
        const model = this.getSelectModel() as IChartModel;
        if (model?.type !== UIModelType.CHART) return '';
        const axis = model.getSelectedElementProperty() as IAxisProperty;
        if (!axis) return;
        return axis.numFmt?.formatCode ?? '';
    };

    /**
     * 获取 等份(主) 值
     */
    public getMajorUnitValue(): number | undefined {
        const model = this.getSelectModel() as IChartModel;
        if (model?.type !== UIModelType.CHART) return;
        const axis = model.getSelectedElementProperty() as IAxisProperty;
        if (!axis) return;
        if (typeof axis?.majorUnit !== 'number') return;
        const majorUnitObj = { majorUnit: axis.majorUnit };
        return this.getShownValue(INPUT_PROPS_CALLBACK_KEY.CHART_VALUE_AXIS, majorUnitObj) as number;
    }

    /**
     * 获取 等份(次) 值
     */
    public getMinorUnitValue(): number | undefined {
        const model = this.getSelectModel() as IChartModel;
        if (model?.type !== UIModelType.CHART) return;
        const axis = model.getSelectedElementProperty() as IAxisProperty;
        if (!axis) return;
        if (typeof axis?.minorUnit !== 'number') return;
        const minorUnitObj = { minorUnit: axis.minorUnit };
        return this.getShownValue(INPUT_PROPS_CALLBACK_KEY.CHART_VALUE_AXIS, minorUnitObj) as number;
    }

    /**
     * 对数 - 底数 是否该置为 disabled 态
     */
    public shouldLogBaseDisabled(): boolean {
        return !this.getLogValue();
    }

    /**
     * 获取 对数 - 对数刻度 值
     */
    public getLogValue(): boolean {
        const model = this.getSelectModel() as IChartModel;
        if (model?.type !== UIModelType.CHART) return false;
        const axis = model.getSelectedElementProperty() as IAxisProperty;
        return !!axis?.scaling?.logBase;
    }

    /**
     * 获取 对数 - 底数 值
     */
    public getLogBaseValue(): number | undefined {
        const logBaseValueObj = { logBaseValue: this.logBaseValue! };
        return this.getShownValue(INPUT_PROPS_CALLBACK_KEY.CHART_VALUE_AXIS, logBaseValueObj) as (number | undefined);
    }

    public getLogBaseInfo() {
        const model = this.getSelectModel() as IChartModel;
        if (model?.type !== UIModelType.CHART) return;
        const axis = model.getSelectedElementProperty() as IAxisProperty;
        if (!axis) return;
        const logBase = axis.scaling?.logBase;
        return logBase;
    }
}
